import {
  BookOpen,
  Compass,
  Eye,
  EyeOff,
  HelpCircle,
  LibraryBig,
  ListChecks,
  MessageCircle,
  Play,
  RotateCcw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { whatsappUrl } from "@/config/site-contact";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

import { useStartTour } from "../hooks";
import { toursFor } from "../registry";
import { useHelpStore } from "../store";

export const HELP_WHATSAPP_MESSAGE =
  "Bonjour GoldenTech, j'utilise MADA et j'ai besoin d'aide sur : ";

/**
 * Menu Aide de la navbar : le point d'entrée unique vers le guide, les
 * visites guidées, les bulles et le contact GoldenTech. Toujours visible,
 * quel que soit le rôle — c'est l'aide, pas une action métier.
 */
export function HelpMenu({ className }: { className?: string }) {
  const me = useAuthStore((s) => s.me);
  const navigate = useNavigate();
  const startTour = useStartTour();
  const beacons = useHelpStore((s) => s.beacons);
  const completed = useHelpStore((s) => s.completedTours);
  const setBeacons = useHelpStore((s) => s.setBeacons);
  const undismissAll = useHelpStore((s) => s.undismissAll);
  const showChecklist = useHelpStore((s) => s.showChecklist);
  const tours = toursFor(me);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Aide"
          title="Aide"
          data-help="nav.help"
          className={cn(
            "text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground",
            className,
          )}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
          <Compass className="h-3.5 w-3.5" aria-hidden /> Aide
        </DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/aide" className="flex cursor-pointer items-center gap-2">
            <BookOpen className="h-4 w-4" aria-hidden /> Guide : démarrer seul avec MADA
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/guide" className="flex cursor-pointer items-center gap-2">
            <LibraryBig className="h-4 w-4" aria-hidden /> Guide d'utilisation complet
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            showChecklist();
            navigate("/dashboard");
          }}
          className="cursor-pointer gap-2"
        >
          <ListChecks className="h-4 w-4" aria-hidden /> Mes premiers pas
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-muted">
          Visites guidées
        </DropdownMenuLabel>
        {tours.map((t) => {
          const done = !!completed[t.id];
          return (
            <DropdownMenuItem
              key={t.id}
              onSelect={() => startTour(t.id)}
              className="cursor-pointer items-start gap-2"
            >
              <Play className={cn("mt-0.5 h-4 w-4 shrink-0", done ? "text-success" : "text-primary")} aria-hidden />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm">{t.title}</span>
                <span className="text-xs text-muted">
                  {t.minutes} min{done ? " · déjà suivie" : ""}
                </span>
              </span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => (beacons ? setBeacons(false) : undismissAll())}
          className="cursor-pointer gap-2"
        >
          {beacons ? (
            <>
              <EyeOff className="h-4 w-4" aria-hidden /> Masquer les bulles d'aide
            </>
          ) : (
            <>
              <Eye className="h-4 w-4" aria-hidden /> Réafficher toutes les bulles
            </>
          )}
        </DropdownMenuItem>
        {beacons && (
          <DropdownMenuItem onSelect={undismissAll} className="cursor-pointer gap-2">
            <RotateCcw className="h-4 w-4" aria-hidden /> Réafficher les bulles fermées
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a
            href={whatsappUrl(HELP_WHATSAPP_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex cursor-pointer items-center gap-2"
          >
            <MessageCircle className="h-4 w-4 text-[#25D366]" aria-hidden /> Écrire à GoldenTech
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
